import { useState } from 'react';
import { Package } from 'lucide-react';
import type { SalesProductPerformance } from '@/types/sales-performance.types';
import sortIcon from '@/assets/sales-performance/chevrons-up-down-filled.svg';
import { count, percent } from './format';

/**
 * Figma Kim-AI 6532:61618 — the products-recommended table. It runs edge to edge
 * of the tab (the design's 1232px frame), so the caller's 16px gutter is undone here.
 */

type SortKey = 'shown' | 'addedToCart' | 'orders' | 'conversionRate';
const PAGE_SIZE = 20;
const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'shown', label: 'Times Shown' },
  { key: 'addedToCart', label: 'Add to cart' },
  { key: 'orders', label: 'Orders' },
  { key: 'conversionRate', label: 'Conversion' },
];

function HeaderCell({ label, divider, sort, onSort, className }: { label: string; divider: boolean; sort?: 'ascending' | 'descending' | 'none'; onSort?: () => void; className: string }) {
  const content = <>
    <span className='flex h-6 items-center gap-2.5'>
      {divider && <span aria-hidden className='h-4 w-px rounded-full bg-gray-500' />}
      <span className='text-xs font-medium leading-4 whitespace-nowrap text-gray-800'>{label}</span>
    </span>
    <img src={sortIcon} alt='' width={14} height={14} className='shrink-0' />
  </>;
  return <div role='columnheader' aria-sort={sort} className={className}>
    {onSort
      ? <button type='button' onClick={onSort} className='flex w-full cursor-pointer items-center justify-between py-2 pr-3'>{content}</button>
      : <div className='flex w-full items-center justify-between py-2 pr-3'>{content}</div>}
  </div>;
}

function MetricCell({ value }: { value: string }) {
  return <div role='cell' className='flex min-w-0 flex-1 items-center'>
    <span aria-hidden className='h-3 w-px shrink-0 rounded-full bg-gray-300' />
    <span className='py-1 pl-3 text-xs font-medium leading-[1.1] whitespace-nowrap text-ink'>{value}</span>
  </div>;
}

export function PerformanceProducts({ products }: { products: SalesProductPerformance[] }) {
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'shown', descending: true });
  const [page, setPage] = useState(0);
  const sorted = [...products].sort((a, b) => ((a[sort.key] ?? -1) - (b[sort.key] ?? -1)) * (sort.descending ? -1 : 1) || a.id.localeCompare(b.id));
  const pageCount = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const toggleSort = (key: SortKey): void => { setSort({ key, descending: sort.key === key ? !sort.descending : true }); setPage(0); };

  return <section aria-label='Products recommended' className='-mx-4 overflow-x-auto bg-white'>
    <div role='table' className='flex min-w-[900px] flex-col gap-1'>
      <div role='row' className='flex border-y border-gray-300 px-4'>
        <div className='flex min-w-0 flex-1 items-center gap-2.5 overflow-clip'>
          <HeaderCell label='Products' divider={false} className='w-[555px] shrink-0' />
          {COLUMNS.map((column) => <HeaderCell key={column.key} label={column.label} divider sort={sort.key === column.key ? sort.descending ? 'descending' : 'ascending' : 'none'} onSort={() => toggleSort(column.key)} className='min-w-0 flex-1' />)}
        </div>
      </div>
      <div className='flex flex-col'>
        {sorted.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE).map((product, index) => <div key={product.id}>
          <div role='row' className='flex h-11 items-center px-4'>
            <div className='flex h-11 min-w-0 flex-1 items-center gap-2.5 overflow-clip py-0.5 pr-0.5'>
              <div role='cell' className='flex w-[552px] shrink-0 items-center gap-1'>
                <span className='w-7 shrink-0 text-xs text-gray-800'>{currentPage * PAGE_SIZE + index + 1}.</span>
                <span className='flex h-10 w-[520px] items-center gap-2.5'>
                  <span className='flex size-10 shrink-0 items-center justify-center overflow-clip rounded-xl bg-white'>
                    {product.imageUrl ? <img src={product.imageUrl} alt='' className='size-10 object-cover' loading='lazy' /> : <Package size={24} className='text-gray-700' aria-hidden />}
                  </span>
                  <span className='min-w-0 truncate py-2 pr-3 text-sm leading-5 text-ink'>{product.title}</span>
                </span>
              </div>
              <MetricCell value={count(product.shown)} />
              <MetricCell value={count(product.addedToCart)} />
              <MetricCell value={count(product.orders)} />
              <MetricCell value={percent(product.conversionRate)} />
            </div>
          </div>
          <hr aria-hidden className='h-px w-full border-0 bg-gray-300' />
        </div>)}
      </div>
    </div>
    {!products.length && <p className='p-10 text-center text-sm text-gray-800'>No products recommended in this period.</p>}
    {products.length > PAGE_SIZE && <div className='flex items-center justify-end gap-4 px-4 py-3 text-xs text-ink'>
      <button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} className='cursor-pointer disabled:opacity-40'>Previous</button>
      <span>{currentPage + 1} / {pageCount}</span>
      <button disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)} className='cursor-pointer disabled:opacity-40'>Next</button>
    </div>}
  </section>;
}
